import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { API, resolveImageUrl } from "@/config";
import {
  Hammer, Home, Layers, RefreshCw, Phone, Mail, MapPin, Star,
  Plus, X, Menu, ChevronRight, Building2, Clock, Award, Trash2,
  Film, Settings, Upload, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import Footer from "@/components/Footer";
import WhatsAppButton from "@/components/WhatsAppButton";
import { useNavigate } from "react-router-dom";

// Language Toggle Button
const LanguageToggle = () => {
  const { lang, toggleLanguage } = useLanguage();
  return (
    <button
      onClick={toggleLanguage}
      data-testid="language-toggle"
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#27272A] hover:border-[#FF6B00] transition-colors text-sm font-medium"
      title={lang === "nl" ? "Switch to English" : "Schakel naar Nederlands"}
    >
      <Globe className="w-3.5 h-3.5 text-[#FF6B00]" />
      <span className="text-white uppercase">{lang === "nl" ? "EN" : "NL"}</span>
    </button>
  );
};

// Navigation Component
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: t("nav_home"), href: "#home", testId: "nav-home" },
    { label: t("nav_services"), href: "#diensten", testId: "nav-diensten" },
    { label: t("nav_about"), href: "#over", testId: "nav-over" },
    { label: t("nav_projects"), href: "#projecten", testId: "nav-projecten" },
    { label: t("nav_reviews"), href: "#reviews", testId: "nav-reviews" },
    { label: t("nav_contact"), href: "#contact", testId: "nav-contact" },
  ];

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#home" className="flex items-center gap-3" data-testid="logo">
          <div className="relative">
            <div className="w-12 h-12 bg-gradient-to-br from-[#FF6B00] to-[#FF8533] rounded-xl flex items-center justify-center shadow-lg shadow-[#FF6B00]/20">
              <span className="text-white font-['Outfit'] font-bold text-xl tracking-tighter">EG</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[#0A0A0A] border-2 border-[#FF6B00] rounded-md flex items-center justify-center">
              <Hammer className="w-2 h-2 text-[#FF6B00]" />
            </div>
          </div>
          <div className="flex flex-col">
            <span className="text-xl font-bold text-white font-['Outfit'] tracking-tight leading-none">EG BOUW</span>
            <span className="text-[10px] text-[#A1A1AA] tracking-[0.2em] uppercase">Vakmanschap sinds 2005</span>
          </div>
        </a>

        <div className="hidden md:flex items-center gap-8">
          {navItems.map((item) => (
            <a key={item.testId} href={item.href} data-testid={item.testId}
              className="text-[#A1A1AA] hover:text-white transition-colors text-sm font-medium">{item.label}</a>
          ))}
          <LanguageToggle />
          <a href="#offerte">
            <Button data-testid="nav-cta-button" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold px-6">
              {t("nav_cta")}
            </Button>
          </a>
        </div>

        <div className="flex md:hidden items-center gap-3">
          <LanguageToggle />
          <button data-testid="mobile-menu-button" className="text-white"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-[#0A0A0A]/95 backdrop-blur-lg border-t border-[#27272A] mt-2">
          <div className="px-6 py-4 space-y-4">
            {navItems.map((item) => (
              <a key={item.testId} href={item.href} data-testid={`mobile-${item.testId}`}
                className="block text-[#A1A1AA] hover:text-white transition-colors text-lg"
                onClick={() => setIsMobileMenuOpen(false)}>{item.label}</a>
            ))}
            <a href="#offerte" onClick={() => setIsMobileMenuOpen(false)}>
              <Button className="w-full bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold mt-4">
                {t("nav_cta")}
              </Button>
            </a>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = ({ content }) => {
  const { t } = useLanguage();
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen flex items-center justify-center"
      style={{ backgroundImage: `url('${content.hero_image}')`, backgroundSize: "cover", backgroundPosition: "center" }}>
      <div className="hero-overlay absolute inset-0"></div>
      <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-[#1A1A1A]/80 border border-[#27272A] rounded-full px-4 py-2 mb-8 animate-fadeIn">
          <Award className="w-4 h-4 text-[#FF6B00]" />
          <span className="text-sm text-[#A1A1AA]">{content.years_experience} {t("hero_badge")}</span>
        </div>
        <h1 data-testid="hero-title"
          className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 font-['Outfit'] tracking-tight leading-tight">
          {t("hero_title")}{" "}<span className="gradient-text">{t("hero_title_accent")}</span>
        </h1>
        <p className="text-lg sm:text-xl text-[#A1A1AA] mb-10 max-w-2xl mx-auto">{t("hero_subtitle")}</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <a href="#offerte">
            <Button data-testid="hero-cta-button" className="btn-primary bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold px-8 py-6 text-lg">
              {t("hero_cta")}<ChevronRight className="ml-2 w-5 h-5" />
            </Button>
          </a>
          <a href="#projecten">
            <Button data-testid="hero-secondary-button" variant="outline" className="border-[#27272A] text-white hover:bg-[#1A1A1A] px-8 py-6 text-lg">
              {t("hero_secondary")}
            </Button>
          </a>
        </div>
      </div>
      <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-[#A1A1AA] rounded-full flex justify-center pt-2">
          <div className="w-1 h-2 bg-[#FF6B00] rounded-full"></div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();
  const services = [
    { id: "timmerwerken", icon: Hammer, title: t("service_timmer"), description: t("service_timmer_desc") },
    { id: "nieuwbouw", icon: Home, title: t("service_nieuwbouw"), description: t("service_nieuwbouw_desc") },
    { id: "wanden-plafonds", icon: Layers, title: t("service_wanden"), description: t("service_wanden_desc") },
    { id: "renovatie", icon: RefreshCw, title: t("service_renovatie"), description: t("service_renovatie_desc") },
    { id: "cinemawalls", icon: Film, title: t("service_cinema"), description: t("service_cinema_desc") },
  ];

  return (
    <section id="diensten" data-testid="services-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("services_label")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-['Outfit'] tracking-tight">{t("services_title")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 stagger-children">
          {services.map((service) => (
            <div key={service.id} data-testid={`service-card-${service.id}`}
              className="service-card bg-[#1A1A1A] rounded-xl p-8 group cursor-pointer">
              <div className="w-14 h-14 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#FF6B00]/20 transition-colors">
                <service.icon className="w-7 h-7 text-[#FF6B00]" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3 font-['Outfit']">{service.title}</h3>
              <p className="text-[#A1A1AA] leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ content }) => {
  const { t } = useLanguage();
  return (
    <section id="over" data-testid="about-section" className="py-24 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#FF6B00]/20 to-transparent rounded-2xl blur-2xl"></div>
              <img src={content.about_image} alt={content.owner_name} data-testid="about-image"
                className="relative rounded-2xl w-full aspect-square object-cover object-top border-2 border-[#27272A]" />
            </div>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("about_label")}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t("about_title")}</h2>
            <p className="text-[#A1A1AA] leading-relaxed text-lg">{t("about_text1")}</p>
            <p className="text-[#A1A1AA] leading-relaxed">{t("about_text2")}</p>
            <div className="flex items-center gap-4 pt-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#FF6B00] to-[#FF8533] rounded-xl flex items-center justify-center">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <div>
                <p className="text-white font-bold text-lg font-['Outfit']">{content.owner_name}</p>
                <p className="text-[#A1A1AA]">{t("owner_title")}</p>
              </div>
              <div className="ml-auto flex items-center gap-2 bg-[#0A0A0A] border border-[#27272A] rounded-xl px-4 py-3" data-testid="vca-badge">
                <svg viewBox="0 0 60 60" className="w-12 h-12 flex-shrink-0">
                  <circle cx="30" cy="30" r="28" fill="#006B3F" stroke="#00A86B" strokeWidth="2"/>
                  <text x="30" y="24" textAnchor="middle" fill="white" fontSize="11" fontWeight="bold" fontFamily="Arial">VCA</text>
                  <line x1="12" y1="29" x2="48" y2="29" stroke="white" strokeWidth="1.5"/>
                  <text x="30" y="42" textAnchor="middle" fill="#FFD700" fontSize="13" fontWeight="bold" fontFamily="Arial">VOL</text>
                </svg>
                <div>
                  <p className="text-white font-bold text-sm font-['Outfit']">VCA VOL</p>
                  <p className="text-[#A1A1AA] text-xs">Gecertificeerd</p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-6 pt-6 border-t border-[#27272A]">
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-[#FF6B00]" />
                <div>
                  <p className="text-2xl font-bold text-white font-['Outfit']">{content.years_experience}</p>
                  <p className="text-sm text-[#A1A1AA]">{t("about_years")}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Award className="w-6 h-6 text-[#FF6B00]" />
                <div>
                  <p className="text-2xl font-bold text-white font-['Outfit']">{content.happy_clients}</p>
                  <p className="text-sm text-[#A1A1AA]">{t("about_clients")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Projects Section
const ProjectsSection = ({ projects, onProjectAdded, onProjectDeleted, isAdmin }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ title: "", description: "", image_url: "", category: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const { t } = useLanguage();

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewProject({ ...newProject, image_url: response.data.url });
      toast.success("Afbeelding geupload!");
    } catch (error) {
      toast.error("Upload mislukt - log eerst in");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post(`${API}/projects`, newProject);
      toast.success("Project toegevoegd!");
      setNewProject({ title: "", description: "", image_url: "", category: "" });
      setIsDialogOpen(false);
      onProjectAdded(response.data);
    } catch (error) {
      toast.error("Fout bij toevoegen project - log eerst in");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (projectId) => {
    try {
      await axios.delete(`${API}/projects/${projectId}`);
      toast.success("Project verwijderd!");
      onProjectDeleted(projectId);
    } catch (error) {
      toast.error("Fout bij verwijderen project - log eerst in");
    }
  };

  return (
    <section id="projecten" data-testid="projects-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-16">
          <div>
            <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("projects_label")}</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-['Outfit'] tracking-tight">{t("projects_title")}</h2>
          </div>
          {isAdmin && (
            <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="add-project-button" className="bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold">
                  <Plus className="w-4 h-4 mr-2" />{t("projects_add")}
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1A1A1A] border-[#27272A] text-white">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit'] text-xl">{t("projects_dialog_title")}</DialogTitle>
                  <DialogDescription className="text-[#A1A1AA]">{t("projects_dialog_desc")}</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div>
                    <Label htmlFor="title">{t("projects_field_title")}</Label>
                    <Input id="title" data-testid="project-title-input" value={newProject.title}
                      onChange={(e) => setNewProject({ ...newProject, title: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white mt-2" required />
                  </div>
                  <div>
                    <Label htmlFor="description">{t("projects_field_desc")}</Label>
                    <Textarea id="description" data-testid="project-description-input" value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white mt-2" required />
                  </div>
                  <div>
                    <Label>{t("projects_field_image")}</Label>
                    <label className="block cursor-pointer mt-2">
                      <div className="flex items-center justify-center gap-2 bg-[#0A0A0A] border border-[#27272A] border-dashed rounded-lg p-4 hover:border-[#FF6B00] transition-colors">
                        <Upload className="w-5 h-5 text-[#A1A1AA]" />
                        <span className="text-[#A1A1AA]">{uploadingImage ? t("projects_uploading") : t("projects_field_image_btn")}</span>
                      </div>
                      <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
                    </label>
                    {newProject.image_url && <img src={newProject.image_url} alt="Preview" className="mt-2 h-24 rounded-lg object-cover" />}
                    <Input data-testid="project-image-input" value={newProject.image_url}
                      onChange={(e) => setNewProject({ ...newProject, image_url: e.target.value })}
                      className="bg-[#0A0A0A] border-[#27272A] text-white mt-2" placeholder={t("projects_field_image_url")} />
                  </div>
                  <div>
                    <Label htmlFor="category">{t("projects_field_category")}</Label>
                    <Select value={newProject.category} onValueChange={(value) => setNewProject({ ...newProject, category: value })}>
                      <SelectTrigger data-testid="project-category-select" className="bg-[#0A0A0A] border-[#27272A] text-white mt-2">
                        <SelectValue placeholder={t("projects_field_category_placeholder")} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                        <SelectItem value="Timmerwerken">{t("service_timmer")}</SelectItem>
                        <SelectItem value="Nieuwbouw">{t("service_nieuwbouw")}</SelectItem>
                        <SelectItem value="Wanden&plafonds">{t("service_wanden")}</SelectItem>
                        <SelectItem value="Renovatie">{t("service_renovatie")}</SelectItem>
                        <SelectItem value="Cinemawalls">{t("service_cinema")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" data-testid="submit-project-button"
                    className="w-full bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold"
                    disabled={isSubmitting || !newProject.image_url}>
                    {isSubmitting ? t("projects_adding") : t("projects_submit")}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => (
            <div key={project.id} data-testid={`project-card-${project.id}`}
              className="group relative bg-[#1A1A1A] rounded-xl overflow-hidden border border-[#27272A] hover:border-[#FF6B00] transition-all duration-300">
              <div className="img-zoom aspect-video">
                <img src={project.image_url} alt={project.title} className="w-full h-full object-cover" />
              </div>
              <div className="p-6">
                <span className="text-[#FF6B00] text-xs font-semibold uppercase tracking-wider">{project.category}</span>
                <h3 className="text-xl font-bold text-white mt-2 font-['Outfit']">{project.title}</h3>
                <p className="text-[#A1A1AA] mt-2 text-sm">{project.description}</p>
              </div>
              {isAdmin && (
                <button onClick={() => handleDelete(project.id)} data-testid={`delete-project-${project.id}`}
                  className="absolute top-3 right-3 p-2 bg-red-500/80 hover:bg-red-500 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 className="w-4 h-4 text-white" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Reviews Section
const ReviewsSection = ({ reviews }) => {
  const { t } = useLanguage();
  return (
    <section id="reviews" data-testid="reviews-section" className="py-24 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="text-center">
          <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("reviews_label")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-['Outfit'] tracking-tight">{t("reviews_title")}</h2>
          <p className="text-[#A1A1AA] mt-4 md:hidden">{t("reviews_swipe")} &rarr;</p>
        </div>
      </div>
      <div className="md:hidden overflow-x-auto scrollbar-hide px-6 -mx-6">
        <div className="flex gap-4 px-6 pb-4" style={{ width: 'max-content' }}>
          {reviews.map((review) => (
            <div key={review.id || review.name} data-testid={`review-card-mobile-${review.name}`}
              className="review-card flex-shrink-0 w-[300px] p-6 rounded-xl">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#FF6B00] text-[#FF6B00]" />)}
              </div>
              <p className="text-[#A1A1AA] mb-4 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
              <p className="text-white font-semibold font-['Outfit']">{review.name}</p>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:block relative overflow-hidden">
        <div className="flex animate-marquee">
          {[...reviews, ...reviews].map((review, index) => (
            <div key={`${review.id || review.name}-${index}`} data-testid={`review-card-${review.name}`}
              className="review-card flex-shrink-0 w-[350px] mx-3 p-6 rounded-xl">
              <div className="flex items-center gap-1 mb-4">
                {[...Array(review.rating)].map((_, i) => <Star key={i} className="w-5 h-5 fill-[#FF6B00] text-[#FF6B00]" />)}
              </div>
              <p className="text-[#A1A1AA] mb-4 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
              <p className="text-white font-semibold font-['Outfit']">{review.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Quote Form Section
const QuoteFormSection = () => {
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/quotes`, formData);
      toast.success(t("quote_success"));
      setFormData({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (error) {
      toast.error(t("quote_error"));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="offerte" data-testid="quote-section" className="py-24 bg-[#0A0A0A]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("quote_label")}</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-4 font-['Outfit'] tracking-tight">{t("quote_title")}</h2>
          <p className="text-[#A1A1AA] mt-4">{t("quote_subtitle")}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="name" className="text-white">{t("quote_name")} *</Label>
              <Input id="name" data-testid="quote-name-input" value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-[#1A1A1A] border-[#27272A] text-white mt-2 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" required />
            </div>
            <div>
              <Label htmlFor="email" className="text-white">{t("quote_email")} *</Label>
              <Input id="email" type="email" data-testid="quote-email-input" value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-[#1A1A1A] border-[#27272A] text-white mt-2 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" required />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="phone" className="text-white">{t("quote_phone")} *</Label>
              <Input id="phone" type="tel" data-testid="quote-phone-input" value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-[#1A1A1A] border-[#27272A] text-white mt-2 focus:border-[#FF6B00] focus:ring-[#FF6B00]/20" required />
            </div>
            <div>
              <Label htmlFor="service" className="text-white">{t("quote_service")} *</Label>
              <Select value={formData.service} onValueChange={(value) => setFormData({ ...formData, service: value })}>
                <SelectTrigger data-testid="quote-service-select" className="bg-[#1A1A1A] border-[#27272A] text-white mt-2">
                  <SelectValue placeholder={t("quote_service_placeholder")} />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#27272A]">
                  <SelectItem value="Timmerwerken">{t("service_timmer")}</SelectItem>
                  <SelectItem value="Nieuwbouw">{t("service_nieuwbouw")}</SelectItem>
                  <SelectItem value="Wanden & Plafonds">{t("service_wanden")}</SelectItem>
                  <SelectItem value="Renovatie">{t("service_renovatie")}</SelectItem>
                  <SelectItem value="Cinemawalls">{t("service_cinema")}</SelectItem>
                  <SelectItem value="Anders">{t("quote_other")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="message" className="text-white">{t("quote_message")} *</Label>
            <Textarea id="message" data-testid="quote-message-input" value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="bg-[#1A1A1A] border-[#27272A] text-white mt-2 min-h-[150px] focus:border-[#FF6B00] focus:ring-[#FF6B00]/20"
              placeholder={t("quote_message_placeholder")} required />
          </div>
          <Button type="submit" data-testid="submit-quote-button"
            className="w-full btn-primary bg-[#FF6B00] hover:bg-[#FF8533] text-white font-semibold py-6 text-lg"
            disabled={isSubmitting}>
            {isSubmitting ? t("quote_submitting") : t("quote_submit")}
          </Button>
        </form>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  return (
    <section id="contact" data-testid="contact-section" className="py-24 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <span className="text-[#FF6B00] text-sm font-semibold uppercase tracking-wider">{t("contact_label")}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mt-4 mb-8 font-['Outfit'] tracking-tight">{t("contact_title")}</h2>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center flex-shrink-0"><MapPin className="w-6 h-6 text-[#FF6B00]" /></div>
                <div><p className="text-white font-semibold">{t("contact_address")}</p><p className="text-[#A1A1AA]">Torenallee 101, 5617BR Eindhoven</p></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center flex-shrink-0"><Phone className="w-6 h-6 text-[#FF6B00]" /></div>
                <div><p className="text-white font-semibold">{t("contact_phone")}</p><a href="tel:0681025105" className="text-[#A1A1AA] hover:text-[#FF6B00] transition-colors">06 81 02 51 05</a></div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#FF6B00]/10 rounded-xl flex items-center justify-center flex-shrink-0"><Mail className="w-6 h-6 text-[#FF6B00]" /></div>
                <div><p className="text-white font-semibold">{t("contact_email")}</p><a href="mailto:info@ella-gold.com" className="text-[#A1A1AA] hover:text-[#FF6B00] transition-colors">info@ella-gold.com</a></div>
              </div>
            </div>
          </div>
          <div className="bg-[#0A0A0A] rounded-2xl p-8 border border-[#27272A]">
            <h3 className="text-xl font-bold text-white mb-6 font-['Outfit']">{t("contact_business")}</h3>
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-[#27272A]"><span className="text-[#A1A1AA]">{t("contact_kvk")}</span><span className="text-white font-medium">99681293</span></div>
              <div className="flex justify-between py-3 border-b border-[#27272A]"><span className="text-[#A1A1AA]">{t("contact_btw")}</span><span className="text-white font-medium">NL005404414B27</span></div>
              <div className="flex justify-between py-3"><span className="text-[#A1A1AA]">IBAN</span><span className="text-white font-medium">NL22INGB0116174293</span></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Admin Button
const AdminButton = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  if (loading || !user || user === false) return null;
  return (
    <button onClick={() => navigate('/admin')} data-testid="admin-button"
      className="fixed bottom-6 left-6 z-50 w-12 h-12 bg-[#1A1A1A] border border-[#27272A] hover:border-[#FF6B00] rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all"
      title="Admin Panel"><Settings className="w-5 h-5 text-[#A1A1AA] hover:text-[#FF6B00]" /></button>
  );
};

// Home Page Component
const HomePage = () => {
  const [projects, setProjects] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    const initData = async () => {
      try {
        await axios.post(`${API}/seed-reviews`);
        await axios.post(`${API}/seed-projects`);
        const [projectsRes, reviewsRes, contentRes] = await Promise.all([
          axios.get(`${API}/projects`), axios.get(`${API}/reviews`), axios.get(`${API}/content`),
        ]);
        setProjects(projectsRes.data.map(p => ({ ...p, image_url: resolveImageUrl(p.image_url) })));
        setReviews(reviewsRes.data);
        setContent({ ...contentRes.data, hero_image: resolveImageUrl(contentRes.data.hero_image), about_image: resolveImageUrl(contentRes.data.about_image) });
      } catch (error) {
        toast.error("Fout bij laden van data");
      } finally {
        setLoading(false);
      }
    };
    initData();
  }, []);

  const handleProjectAdded = useCallback((newProj) => {
    setProjects(prev => [...prev, { ...newProj, image_url: resolveImageUrl(newProj.image_url) }]);
  }, []);

  const handleProjectDeleted = useCallback((projectId) => {
    setProjects(prev => prev.filter((p) => p.id !== projectId));
  }, []);

  if (loading || !content) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#FF6B00] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="App bg-[#0A0A0A] min-h-screen">
      <Toaster position="top-right" theme="dark" />
      <Navigation />
      <HeroSection content={content} />
      <ServicesSection />
      <AboutSection content={content} />
      <ProjectsSection projects={projects} onProjectAdded={handleProjectAdded} onProjectDeleted={handleProjectDeleted} isAdmin={!!user} />
      <ReviewsSection reviews={reviews} />
      <QuoteFormSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default HomePage;
